import React, { useState, useEffect, useRef } from 'react'
import { font } from './constants'
import { getAiSimulationDetails, getOneSimulationDetails, getRunById, getTranslation } from '../../requests'
import PersonaTab from './PersonaTab'
import QueryTab from './QueryTab'
import ResponseTab from './ResponseTab'
import CompetitiveTab from './CompetitiveTab'
import CrossPlatformTab from './CrossPlatformTab'
import DownloadTab from './DownloadTab'
import SimulationRunTab from './SimulationRunTab'
import { notifyError, notifySuccess } from '../../utils/helper'

export const PROJECT_TABS = [
  { id: 'persona', label: 'Persona', icon: '👤' },
  { id: 'queries', label: 'Queries', icon: '🔍' },
  { id: 'response', label: 'Response', icon: '💬' },
  { id: 'competitive', label: 'Competitive', icon: '📊' },
  { id: 'crossplatform', label: 'Cross-Platform', icon: '🧭' },
  { id: 'run', label: 'Runs', icon: '⚡' },
  { id: 'download', label: 'Download', icon: '📥' },
]

const NA = 'Not available'
const FUNNEL_LABEL_TO_ID = { Awareness: 'awareness', Consideration: 'consideration', 'Purchase Intent': 'purchase_intent' }

function safeProjectStr(v) {
  if (v == null) return NA
  const s = typeof v === 'string' ? v.trim() : String(v).trim()
  return s || NA
}

function ProjectDetail({
  project,
  onBack,
  initialTab,
  tabFromUrl,
  simulationDetailPayload = null,
  forcedSimulationId = null,
  onSimulationRefetch = null,
  onNavigateToTab = null,
}) {
  const [activeTab, setActiveTab] = useState(initialTab || 'persona')
  const isPageMode = Boolean(tabFromUrl)
  const currentTab = isPageMode ? tabFromUrl : activeTab
  const activeFunnel = FUNNEL_LABEL_TO_ID[project?.funnel?.[0]] ?? 'awareness'
  const [recentSimulationDetails, setRecentSimulationDetails] = useState(null)
  /** In-memory only: whether API data is shown in English translation ('en') or original ('or'). */
  const [translationViewLang, setTranslationViewLang] = useState('or')
  /** When set (via the Runs tab), every run-scoped tab shows this run's data instead of the latest run. */
  const [selectedRun, setSelectedRun] = useState(null)
  const projectId = project?.id ?? null

  const recentSimulationStatus =
    simulationDetailPayload != null && simulationDetailPayload.status != null
      ? simulationDetailPayload.status
      : null

  const recentSimulationId =
    forcedSimulationId != null && forcedSimulationId !== ''
      ? forcedSimulationId
      : project?.latestSimulationId ?? null

  const prevSimIdForTranslationRef = useRef(recentSimulationId)

  useEffect(() => {
    setSelectedRun(null)
  }, [recentSimulationId])

  // A run opened from the Runs tab is a snapshot. When the live update arrives, replace that
  // snapshot so the platform tabs are not stuck on the copy from before the model rows existed.
  useEffect(() => {
    if (!selectedRun?.id) return
    const fresh = (simulationDetailPayload?.aiSimulationRuns ?? []).find((run) => run.id === selectedRun.id)
    if (fresh && fresh !== selectedRun) setSelectedRun(fresh)
  }, [simulationDetailPayload, selectedRun])

  /** Every run-scoped tab (Response/Competitive/Cross-Platform/Download) reads the "current" run off
   * `aiSimulationRuns[0]` (or via pickLatestAiSimulationRun) — overriding that array here is enough to
   * make them show the selected historical run without touching each tab's internals. */
  const effectiveSimulationDetails = selectedRun
    ? { ...recentSimulationDetails, aiSimulationRuns: [selectedRun] }
    : recentSimulationDetails

  useEffect(() => {
    if (simulationDetailPayload) {
      setRecentSimulationDetails(simulationDetailPayload)
      setTranslationViewLang('or')
      prevSimIdForTranslationRef.current = recentSimulationId
      return
    }
    if (!recentSimulationId) {
      setRecentSimulationDetails(null)
      setTranslationViewLang('or')
      prevSimIdForTranslationRef.current = null
      return
    }
    const simChanged = prevSimIdForTranslationRef.current !== recentSimulationId
    if (simChanged) {
      setTranslationViewLang('or')
      prevSimIdForTranslationRef.current = recentSimulationId
    }
    const showTranslation = simChanged ? false : translationViewLang === 'en'
    getAiSimulationDetails(recentSimulationId, showTranslation)
      .then((res) => {
        setRecentSimulationDetails(res?.data ?? null)
      })
      .catch(() => setRecentSimulationDetails(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- translationViewLang read for refetch; omit from deps to avoid refetch on translate toggle (translateLanguage updates details directly)
  }, [recentSimulationId, recentSimulationStatus, simulationDetailPayload])


  const translateLanguage = async (language = null) => {
    const showTranslation = language === 'en'

    try {
      const response = await getOneSimulationDetails(recentSimulationId, showTranslation)
      if (response?.status < 400) {
        if (response?.data?.customStatusCode === 10001) {
         const res =  await getTranslation(recentSimulationId)
         if(res.data.customStatusCode === 10002){
          notifySuccess("Translation in progress.. you will be notified by mail when it is completed")
          return null;
         }
          notifySuccess('AI translation begins.. you will be notified by mail when it is complete' || response?.data?.message);
          return null;
        }
        notifySuccess('Translation successful' || response?.data?.message);
        const data = response?.data
        if (recentSimulationId != null && recentSimulationId !== '') {
          setTranslationViewLang(showTranslation ? 'en' : 'or')
        }
        setRecentSimulationDetails(data ?? null)
        if (selectedRun?.id) {
          const runRes = await getRunById(selectedRun.id, showTranslation)
          const body = runRes?.data?.data ?? runRes?.data
          if (body) setSelectedRun(body)
        }
        return data
      }


    } catch (error) {
      notifyError('Translation failed' || error?.response?.data?.message);
      return null;

    }
  }





  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'transparent',
              color: '#b0b8c8',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: font,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ← Back to Simulations
          </button>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0, fontFamily: font }}>
              {project ? safeProjectStr(project.name) : NA}
            </h1>
            <p style={{ fontSize: 13, color: '#6b7a90', margin: '4px 0 0', fontFamily: font }}>
              {project ? (() => { const a = [project.brand, project.market, project.category].map(safeProjectStr); return a.filter(p => p !== NA).join(' · ') || NA })() : NA}
            </p>
          </div>
        </div>
      </div> */}

      {!isPageMode && (
        <div
          style={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            paddingBottom: 0,
          }}
        >
          {PROJECT_TABS.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '12px 18px',
                borderRadius: '8px 8px 0 0',
                border: 'none',
                background: currentTab === t.id ? 'rgba(239,68,68,0.1)' : 'transparent',
                color: currentTab === t.id ? '#363636' : '#999999',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: font,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderBottom: currentTab === t.id ? '2px solid #ef4444' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              <span>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ minHeight: 200 }}>
        {currentTab === 'persona' && (
          <PersonaTab
            project={simulationDetailPayload?.generated_persona ?? null}
            audiencePrompt={simulationDetailPayload?.audience_prompt ?? null}
            projectId={projectId}
            recentSimulationStatus={recentSimulationStatus}
            onGoToQueries={() => {
              if (isPageMode && onNavigateToTab) onNavigateToTab('queries')
              else setActiveTab('queries')
            }}
          />
        )}
        {currentTab === 'queries' && (
          <QueryTab
            recentSimulationDetails={recentSimulationDetails}
            recentSimulationStatus={recentSimulationStatus}
            recentSimulationId={recentSimulationId}
            projectId={projectId}
            onRunSuccess={() => onSimulationRefetch?.()}
            translateLanguage={translateLanguage}
            translationViewLang={translationViewLang}
          />
        )}
        {currentTab === 'response' && (
          <ResponseTab
            recentSimulationId={recentSimulationId}
            activeFunnel={activeFunnel}
            recentSimulationDetails={effectiveSimulationDetails}
            recentSimulationStatus={recentSimulationStatus}
            selectedRun={selectedRun}
            onReturnToLatestRun={() => setSelectedRun(null)}
            translateLanguage={translateLanguage}
            translationViewLang={translationViewLang}
            onGoToCompetitive={() => {
              if (isPageMode && onNavigateToTab) onNavigateToTab('competitive')
              else setActiveTab('competitive')
            }}
          />
        )}
        {currentTab === 'competitive' && (
          <CompetitiveTab
            recentSimulationId={recentSimulationId}
            activeFunnel={activeFunnel}
            recentSimulationDetails={effectiveSimulationDetails}
            recentSimulationStatus={recentSimulationStatus}
            selectedRun={selectedRun}
            onReturnToLatestRun={() => setSelectedRun(null)}
            translateLanguage={translateLanguage}
            translationViewLang={translationViewLang}
            onGoToRuns={() => {
              if (isPageMode && onNavigateToTab) onNavigateToTab('run')
              else setActiveTab('run')
            }}
          />
        )}
        {currentTab === 'crossplatform' && (
          <CrossPlatformTab
            recentSimulationId={recentSimulationId}
            recentSimulationDetails={effectiveSimulationDetails}
            recentSimulationStatus={recentSimulationStatus}
            selectedRun={selectedRun}
            onReturnToLatestRun={() => setSelectedRun(null)}
            onGoToRuns={() => {
              if (isPageMode && onNavigateToTab) onNavigateToTab('run')
              else setActiveTab('run')
            }}
          />
        )}
        {currentTab === 'run' && (
          <SimulationRunTab
            recentSimulationId={recentSimulationId}
            recentSimulationDetails={recentSimulationDetails}
            selectedRun={selectedRun}
            translationViewLang={translationViewLang}
            onRunSelected={setSelectedRun}
            onReturnToLatestRun={() => setSelectedRun(null)}
            onNewRun={() => onSimulationRefetch?.()}
            onGoToDownload={() => {
              if (isPageMode && onNavigateToTab) onNavigateToTab('download')
              else setActiveTab('download')
            }}
          />
        )}
        {currentTab === 'download' && (
          <DownloadTab
            recentSimulationId={recentSimulationId}
            recentSimulationStatus={recentSimulationStatus}
            projectId={projectId}
            recentSimulationDetails={effectiveSimulationDetails}
            selectedRun={selectedRun}
            onReturnToLatestRun={() => setSelectedRun(null)}
            translateLanguage={translateLanguage}
            translationViewLang={translationViewLang}
          />
        )}
      </div>
    </div>
  )
}

export default ProjectDetail
